import { useMemo, useRef } from 'react';

import SpaceHr from '../../../components/SpaceHr';
import SpaceY from '../../../components/SpaceY';
import CountUpNumber from '../../../reusable-react/CountupNumber';
import {
  MAX_WIDTH_639PX,
  s3CloudFrontUrlEndpoint,
} from '../../../utils/common';
import { useGetResizedRefSize } from '../../../utils/hook/hook';
import useTotalSavedQuery from '../../../utils/hook/react-query/subscription/useTotalSavedQuery';
import useClientMediaQuery from '../../../utils/hook/useClientMediaQuery';

import { DEFAULT_TOTAL_SAVED_VEG } from './const';

const MissionRescue = () => {
  const isMobile = useClientMediaQuery({ query: MAX_WIDTH_639PX });
  const totalSavedHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const { width: totalSavedHeadingWidth } =
    useGetResizedRefSize(totalSavedHeadingRef);

  const { saved, isLoading } = useTotalSavedQuery();

  const reducedCO2 = useMemo(() => {
    return Math.round(saved.totalSavedVeg * 0.5979).toLocaleString(); // 구출한 농산물 * 59.79%
  }, [saved]);
  const savedWater = useMemo(() => {
    return Math.round(saved.totalSavedVeg * 0.0202).toLocaleString(); // 구출한 농산물 * 2.02%
  }, [saved]);

  const DATA_LIST = [
    { name: '아낀 물의 양', description: 'million litres', data: savedWater },
    {
      name: '아낀 플라스틱 수',
      description: '',
      data: saved.totalSavedPlastic.toLocaleString(),
    },
    { name: '절감한 탄소', description: 'kg', data: reducedCO2 },
  ];

  return (
    <div className="">
      {/* totalSavedHeadingWidth 측정을 위한 transparent */}
      <h1
        ref={totalSavedHeadingRef}
        className="absolute text-right text-30px font-bold text-transparent"
      >
        {saved.totalSavedVeg.toLocaleString()}
      </h1>

      <h3 className="text-center text-22px font-bold leading-[30.8px] text-gray-800 sm:text-26px sm:leading-[36.4px]">
        못난이 농산물 구출이
        <br />
        어떤 변화를 만들고 있을까요?
      </h3>

      <SpaceY height={32} />
      {isLoading && totalSavedHeadingWidth === 0 ? (
        <></>
      ) : isMobile ? (
        <div className="rounded-10px bg-gray-100 py-32px px-24px">
          <h5 className="text-center text-16px text-gray-800">구출한 농산물</h5>

          <SpaceY height={8} />
          <div className="flex-center">
            <div className="flex">
              <div style={{ width: totalSavedHeadingWidth }}>
                <h1 className="text-right text-30px font-bold text-brand-1">
                  <CountUpNumber
                    end={saved.totalSavedVeg}
                    start={DEFAULT_TOTAL_SAVED_VEG}
                  />
                </h1>
              </div>
              <h1 className="text-center text-30px font-bold text-brand-1">
                kg
              </h1>
            </div>
          </div>

          <SpaceHr my={24} />
          <div className="space-y-16px">
            {DATA_LIST.map(({ name, description, data }) => {
              return (
                <div key={name} className="flex-between">
                  <div className="flex gap-4px">
                    <p className="text-18px text-gray-800">{name}</p>
                    <p className="text-10px font-medium">{description}</p>
                  </div>
                  <h4 className="text-18px font-bold text-gray-800">{data}</h4>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex rounded-10px bg-gray-100 px-40px pt-30px pb-28px">
          <div className="flex-center grow">
            <div>
              <h5 className="text-center text-16px text-gray-800">
                구출한 농산물
              </h5>

              <SpaceY height={8} />
              <div className="flex-center">
                <div className="flex">
                  <div style={{ width: totalSavedHeadingWidth }}>
                    <h1 className="text-right text-30px font-bold text-brand-1">
                      <CountUpNumber
                        end={saved.totalSavedVeg}
                        start={DEFAULT_TOTAL_SAVED_VEG}
                      />
                    </h1>
                  </div>
                  <h1 className="text-center text-30px font-bold text-brand-1">
                    kg
                  </h1>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-40px w-1px border-r border-gray-200" />

          <div className="grow space-y-16px">
            {DATA_LIST.map(({ name, description, data }) => {
              return (
                <div key={name} className="flex-between">
                  <div className="flex gap-4px">
                    <p className="text-18px text-gray-800">{name}</p>
                    <p className="text-10px font-medium">{description}</p>
                  </div>
                  <h4 className="text-18px font-bold text-gray-800">{data}</h4>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <SpaceY height={32} />
      <p className="text-18px leading-[28.8px] text-gray-800 sm:text-center">
        농산물의 폐기는 유통과 소비 모든 단계에서 사회적 손실을 야기합니다.
        {isMobile || <br />}
        <span className="font-bold">
          어글리어스는 불필요한 낭비를 줄여 환경 보호에 기여하고, 농부의 노력에
          대한 정당한 보상을 제공합니다.
        </span>
      </p>

      <SpaceY height={32} />
      <div className="relative h-200px overflow-hidden rounded-10px sm:h-280px">
        <img
          src={`${s3CloudFrontUrlEndpoint}/mission/korea_cabbage.png`}
          alt="배추 사진"
          className="h-full w-full object-cover object-center"
        />
        <div
          className="flex-center absolute inset-0 z-10"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        >
          <p className="text-center text-18px font-bold leading-[27px] text-white sm:text-22px sm:leading-[33px]">
            지속가능한 농업을 지지함으로써
            <br />
            우리의 땅과 미래를 더 건강하게 만듭니다.
            <br />
            분명한 변화를 만듭니다.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MissionRescue;
